/**
 * AgentsReferenceScreen — a menu-accessible reference for the four enemy
 * agent archetypes. Surfaces each archetype's deck composition, its AI
 * goal-weight modifiers, and spawn behavior so the design work on tuning
 * these archetypes has a single source of truth to look at.
 *
 * Purely informational; no run state.
 */

import React from 'react';
import { theme } from '../../styles/theme';
import { AGENT_DECK_TEMPLATES, CARD_TEMPLATES } from '../../data/cards';
import { tuning } from '../../data/tuning';
import type { AgentArchetype, CardCategory } from '../../types/game';

interface AgentsReferenceScreenProps {
  onBack: () => void;
}

// Mirrors the category colors used throughout the game UI.
const CATEGORY_COLORS: Record<CardCategory, string> = {
  movement: '#66ccff',
  intel: '#aa88ff',
  tactics: '#ffaa44',
  combat: '#ff5555',
  fieldcraft: '#44cc88',
  junk: '#777777',
};

// --- Archetype metadata ---
// Per-archetype color, display name, narrative description, and the tuning
// key used to look up its AI goal weights (note the 'commander'→'enforcer'
// mismatch baked into current tuning — exposed here verbatim so the
// reference reflects actual runtime behavior, not the intended behavior).
interface ArchetypeInfo {
  key: AgentArchetype;
  name: string;
  color: string;
  /** Short, player-facing description of the archetype's feel. */
  description: string;
  /** Which role this archetype is supposed to fill. */
  role: string;
  /** Tuning key used by the AI to apply goal mods. */
  tuningKey: string;
  /** Human-readable note on spawn cadence. */
  spawnNote: string;
}

const ARCHETYPES: ArchetypeInfo[] = [
  {
    key: 'tracker',
    name: 'Tracker',
    color: '#66ccff',
    description:
      'Methodical pursuer. Weighs closing the gap to the player above all other priorities. Plays a movement-heavy deck with sustained intel sweeps.',
    role: 'Pursuer',
    tuningKey: 'tracker',
    spawnNote: 'R1: 100% · R2: 60% · R3: 40% · R4+: 33%',
  },
  {
    key: 'commander',
    name: 'Commander',
    color: '#ff5555',
    description:
      'Aggressive combat-first archetype. Prep-stacks with Sharpen + Improvised Strike and pushes for confrontation on the flat. High lethality when cornered.',
    role: 'Aggressor',
    // NOTE: tuning currently keys this under 'enforcer' — bug: lookup misses.
    tuningKey: 'enforcer',
    spawnNote: 'R2: 40% · R3: 40% · R4+: 34%',
  },
  {
    key: 'ghost',
    name: 'Ghost',
    color: '#aa88ff',
    description:
      'Evasive trap-layer. Favors repositioning (Shortcut, QuickStep, Fade) to set up ambush geometry rather than closing head-on. Hard to read.',
    role: 'Trap-layer',
    tuningKey: 'ghost',
    spawnNote: 'R3: 20% · R4+: 33%',
  },
  {
    key: 'sniper',
    name: 'Sniper',
    color: '#ffaa44',
    description:
      'Overwatch specialist. Wants elevated position + maintained vision. Spotter-heavy, rooftop-centric. Only triggered when the weapon roll is sniper-rifle.',
    role: 'Overwatch',
    tuningKey: 'sniper',
    // Note the archetype-override bug: weapon=sniper currently forces
    // archetype='tracker', so this archetype is dormant in runtime.
    spawnNote: 'Weapon-gated · currently orphaned by weapon override (see notes)',
  },
];

// --- Helpers ---

interface DeckSummary {
  totalCards: number;
  /** Aggregate of (card name, count, category, ap cost) for this archetype. */
  entries: Array<{
    key: string;
    name: string;
    count: number;
    category: CardCategory;
    apCost: number;
    description: string;
  }>;
  categoryBreakdown: Array<{ category: CardCategory; count: number }>;
}

function summarizeDeck(archetype: AgentArchetype): DeckSummary {
  const keys = AGENT_DECK_TEMPLATES[archetype];
  const counts = new Map<string, number>();
  for (const k of keys) counts.set(k, (counts.get(k) ?? 0) + 1);

  const entries: DeckSummary['entries'] = [];
  const categoryTotals = new Map<CardCategory, number>();
  for (const [key, count] of counts.entries()) {
    const template = CARD_TEMPLATES[key];
    if (!template) continue;
    entries.push({
      key,
      name: template.name,
      count,
      category: template.category,
      apCost: template.apCost,
      description: template.description,
    });
    categoryTotals.set(template.category, (categoryTotals.get(template.category) ?? 0) + count);
  }
  // Sort: highest count first, then AP cost, then name.
  entries.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    if (a.apCost !== b.apCost) return a.apCost - b.apCost;
    return a.name.localeCompare(b.name);
  });

  const categoryBreakdown = Array.from(categoryTotals.entries())
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count);

  return { totalCards: keys.length, entries, categoryBreakdown };
}

function formatGoalMod(value: number): string {
  if (value === 0) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}`;
}

// --- Component ---

export function AgentsReferenceScreen({ onBack }: AgentsReferenceScreenProps) {
  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <div style={styles.headerRow}>
          <button style={styles.backButton} onClick={onBack}>← MENU</button>
          <h1 style={styles.title}>ENEMY AGENTS</h1>
          <div style={styles.headerCount}>{ARCHETYPES.length} ARCHETYPES</div>
        </div>

        <div style={styles.preamble}>
          Each agent draws two things from its archetype: a preset 20-card
          deck, and a set of AI goal-weight modifiers. Goal weights nudge
          the AI's valuation of four competing priorities —
          {' '}<em>prep</em> (build combat readiness),{' '}
          <em>distance</em> (close on the player),{' '}
          <em>intel</em> (reveal player signals), and{' '}
          <em>position</em> (set up ambush geometry). The archetype's deck
          composition further shapes play style by gating which cards the
          agent can even draw.
        </div>

        {ARCHETYPES.map(info => {
          const deck = summarizeDeck(info.key);
          const mods = tuning.agentGoalWeights[info.tuningKey];
          return (
            <section key={info.key} style={{ ...styles.section, borderColor: `${info.color}40` }}>
              {/* Header bar */}
              <div style={{ ...styles.sectionHeader, background: `${info.color}14`, borderColor: `${info.color}55` }}>
                <div style={styles.sectionHeaderLeft}>
                  <span style={{ ...styles.sectionMark, color: info.color }}>▍</span>
                  <span style={{ ...styles.sectionName, color: info.color }}>{info.name.toUpperCase()}</span>
                  <span style={styles.sectionRole}>— {info.role}</span>
                </div>
                <div style={styles.sectionHeaderRight}>
                  <span style={styles.sectionDeckSize}>{deck.totalCards} CARDS</span>
                </div>
              </div>

              <div style={styles.sectionBody}>
                <div style={styles.description}>{info.description}</div>

                <div style={styles.metaGrid}>
                  <MetaBlock label="SPAWN RATE">
                    <div style={styles.metaText}>{info.spawnNote}</div>
                  </MetaBlock>

                  <MetaBlock label="AI GOAL WEIGHTS">
                    {mods ? (
                      <div style={styles.goalRow}>
                        <GoalCell label="prep" value={mods.prep} color={info.color} />
                        <GoalCell label="dist" value={mods.distance} color={info.color} />
                        <GoalCell label="intel" value={mods.intel} color={info.color} />
                        <GoalCell label="pos" value={mods.position} color={info.color} />
                      </div>
                    ) : (
                      <div style={{ ...styles.metaText, color: '#ff8844' }}>
                        ⚠ No weights applied (tuning key <code>{info.tuningKey}</code> not found)
                      </div>
                    )}
                  </MetaBlock>

                  <MetaBlock label="CATEGORY BREAKDOWN">
                    <div style={styles.catRow}>
                      {deck.categoryBreakdown.map(({ category, count }) => (
                        <div
                          key={category}
                          style={{
                            ...styles.catPill,
                            color: CATEGORY_COLORS[category],
                            borderColor: `${CATEGORY_COLORS[category]}55`,
                            background: `${CATEGORY_COLORS[category]}10`,
                          }}
                        >
                          {category.toUpperCase()} ×{count}
                        </div>
                      ))}
                    </div>
                  </MetaBlock>
                </div>

                {/* Deck grid */}
                <div style={styles.deckHeader}>DECK COMPOSITION</div>
                <div style={styles.deckGrid}>
                  {deck.entries.map(entry => {
                    const color = CATEGORY_COLORS[entry.category];
                    return (
                      <div
                        key={entry.key}
                        style={{
                          ...styles.cardEntry,
                          borderColor: `${color}40`,
                          borderLeftColor: color,
                        }}
                      >
                        <div style={styles.cardEntryTop}>
                          <span style={{ ...styles.cardEntryCount, color }}>×{entry.count}</span>
                          <span style={styles.cardEntryName}>{entry.name}</span>
                          <span style={{ ...styles.cardEntryCost, color: theme.colors.accentYellow }}>
                            {entry.apCost} AP
                          </span>
                        </div>
                        <div style={{ ...styles.cardEntryCategory, color }}>
                          {entry.category.toUpperCase()}
                        </div>
                        <div style={styles.cardEntryDesc}>{entry.description}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          );
        })}

        {/* Notes section for the sharp edges in current agent tuning — surfaces
            the two bugs I noticed while reading the code so design work on
            tuning these archetypes can start from a clear picture. */}
        <section style={styles.notesSection}>
          <div style={styles.notesHeader}>IMPLEMENTATION NOTES</div>
          <div style={styles.notesBody}>
            <div style={styles.noteRow}>
              <span style={styles.noteLabel}>⚠ Commander AI mod missing.</span>{' '}
              The archetype rolls as <code>commander</code> but its goal
              weights are keyed under <code>enforcer</code> in{' '}
              <code>tuning.agentGoalWeights</code>. The lookup returns
              undefined and no mods are applied — commander agents fall
              through to base AI weights. Deck-driven aggression still
              works; the AI-side prep bias does not.
            </div>
            <div style={styles.noteRow}>
              <span style={styles.noteLabel}>⚠ Sniper archetype unreachable.</span>{' '}
              When weapon rolls as <code>sniper-rifle</code>,{' '}
              <code>generateAgentLoadout</code> force-sets archetype to{' '}
              <code>tracker</code>, not <code>sniper</code>. The sniper
              deck template and sniper goal weights exist but are never
              used in current gameplay.
            </div>
            <div style={styles.noteRow}>
              <span style={styles.noteLabel}>ℹ Act 1 is always tracker.</span>{' '}
              Map-node pre-rolling uses round 1 for Act 1 nodes, which
              deterministically selects tracker. Variety kicks in at Act 2
              (33/34/33 mix). Intentional per the telegraphed-early /
              uncertain-late pacing, but flagged here so it's not
              confused with a bug.
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

// --- Small layout components ---

function MetaBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={styles.metaBlock}>
      <div style={styles.metaLabel}>{label}</div>
      <div style={styles.metaContent}>{children}</div>
    </div>
  );
}

function GoalCell({ label, value, color }: { label: string; value: number; color: string }) {
  const active = value !== 0;
  return (
    <div style={{
      ...styles.goalCell,
      borderColor: active ? `${color}55` : theme.colors.border,
      background: active ? `${color}14` : 'transparent',
    }}>
      <span style={styles.goalCellLabel}>{label}</span>
      <span style={{
        ...styles.goalCellValue,
        color: active ? color : theme.colors.textDim,
      }}>
        {formatGoalMod(value)}
      </span>
    </div>
  );
}

// --- Styles ---

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(8, 12, 20, 0.98)',
    overflow: 'auto',
    fontFamily: theme.fonts.mono,
  },
  inner: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '32px 32px 64px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  backButton: {
    background: 'rgba(255, 255, 255, 0.03)',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textSecondary,
    fontFamily: theme.fonts.mono,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '2px',
    padding: '8px 14px',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    letterSpacing: '6px',
    color: theme.colors.textPrimary,
    margin: 0,
    flex: 1,
  },
  headerCount: {
    fontSize: '10px',
    letterSpacing: '2px',
    color: theme.colors.textDim,
    fontWeight: 600,
  },
  preamble: {
    fontSize: '12px',
    lineHeight: 1.6,
    color: theme.colors.textSecondary,
    background: 'rgba(255, 255, 255, 0.02)',
    border: `1px solid ${theme.colors.border}`,
    padding: '14px 18px',
    borderRadius: '6px',
    marginBottom: '32px',
  },
  section: {
    background: 'rgba(12, 16, 24, 0.5)',
    border: '1px solid',
    borderRadius: '8px',
    marginBottom: '28px',
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid',
  },
  sectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  sectionHeaderRight: {
    display: 'flex',
    alignItems: 'center',
  },
  sectionMark: {
    fontSize: '18px',
    lineHeight: 1,
  },
  sectionName: {
    fontSize: '15px',
    fontWeight: 700,
    letterSpacing: '4px',
  },
  sectionRole: {
    fontSize: '10px',
    letterSpacing: '2px',
    color: theme.colors.textDim,
    fontWeight: 600,
  },
  sectionDeckSize: {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '2px',
    color: theme.colors.textSecondary,
  },
  sectionBody: {
    padding: '18px 20px 20px',
  },
  description: {
    fontSize: '12px',
    color: theme.colors.textPrimary,
    lineHeight: 1.6,
    marginBottom: '18px',
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '12px',
    marginBottom: '22px',
  },
  metaBlock: {
    background: 'rgba(255, 255, 255, 0.02)',
    border: `1px solid ${theme.colors.border}`,
    padding: '10px 12px',
    borderRadius: '4px',
  },
  metaLabel: {
    fontSize: '9px',
    letterSpacing: '2px',
    fontWeight: 700,
    color: theme.colors.textDim,
    marginBottom: '6px',
  },
  metaContent: {
    fontSize: '11px',
  },
  metaText: {
    fontSize: '11px',
    color: theme.colors.textSecondary,
    lineHeight: 1.5,
  },
  goalRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '6px',
  },
  goalCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '6px 4px',
    border: '1px solid',
    borderRadius: '3px',
  },
  goalCellLabel: {
    fontSize: '8px',
    letterSpacing: '1px',
    color: theme.colors.textDim,
    fontWeight: 700,
    marginBottom: '2px',
  },
  goalCellValue: {
    fontSize: '13px',
    fontWeight: 700,
    fontFamily: theme.fonts.mono,
  },
  catRow: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  catPill: {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '1px',
    padding: '4px 8px',
    border: '1px solid',
    borderRadius: '3px',
  },
  deckHeader: {
    fontSize: '9px',
    letterSpacing: '2px',
    fontWeight: 700,
    color: theme.colors.textDim,
    marginBottom: '10px',
  },
  deckGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '8px',
  },
  cardEntry: {
    background: 'rgba(12, 16, 24, 0.6)',
    border: '1px solid',
    borderLeft: '3px solid',
    borderRadius: '3px',
    padding: '8px 12px',
  },
  cardEntryTop: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    marginBottom: '2px',
  },
  cardEntryCount: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    minWidth: '24px',
  },
  cardEntryName: {
    fontSize: '12px',
    fontWeight: 700,
    color: theme.colors.textPrimary,
    flex: 1,
  },
  cardEntryCost: {
    fontSize: '10px',
    fontWeight: 700,
  },
  cardEntryCategory: {
    fontSize: '8px',
    letterSpacing: '1.5px',
    fontWeight: 700,
    marginBottom: '4px',
  },
  cardEntryDesc: {
    fontSize: '10px',
    lineHeight: 1.4,
    color: theme.colors.textSecondary,
  },
  notesSection: {
    marginTop: '40px',
    background: 'rgba(255, 160, 80, 0.04)',
    border: '1px solid rgba(255, 160, 80, 0.25)',
    borderRadius: '6px',
    padding: '16px 20px',
  },
  notesHeader: {
    fontSize: '10px',
    letterSpacing: '3px',
    fontWeight: 700,
    color: '#ffaa55',
    marginBottom: '10px',
  },
  notesBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  noteRow: {
    fontSize: '11px',
    lineHeight: 1.6,
    color: theme.colors.textSecondary,
  },
  noteLabel: {
    color: '#ffaa55',
    fontWeight: 700,
  },
};
